/**
 * A long Room video is made the way a webinar's recording is: an adaptive stream at
 * `hls/<noteId>/master.m3u8`. Every note player plays that address through here:
 * hls.js where Media Source Extensions work (a code-split chunk, fetched only when a
 * stream shows up), the element's own player on Safari without them. A short note's
 * `.mp4` / `.m4a` is still one file, and stays the element's own `src`.
 *
 * The token rides the master request only (`?t=`), which sets the media host's
 * `fw_media` cookie; every later playlist and segment request carries that cookie
 * (withCredentials / use-credentials), exactly as a webinar replay does.
 */
import { useEffect, type RefObject } from "react";
import type Hls from "hls.js";
import { mediaUrl } from "../board/hls";

/** Is this playable address a stream rather than one file? */
export function isStreamUrl(url: string | null | undefined): boolean {
  return !!url && /\.m3u8(?:[?#]|$)/i.test(url);
}

/**
 * The seconds a note's length label (`M:SS`, as `fmtClock` writes it) stands for, or 0.
 * Why: a stream reads its own length only once it loads, which waits for the play,
 * so until then a player shows the length the attachment carries.
 */
export function clockSeconds(label: string | null | undefined): number {
  const m = /^(\d+):([0-5]\d)$/.exec(label?.trim() ?? "");
  return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
}

/** `…/master.m3u8?t=abc` → the bare address and the token it carried. */
export function splitMediaToken(src: string): { url: string; token: string | null } {
  let u: URL;
  try {
    u = new URL(src);
  } catch {
    return { url: src, token: null };
  }
  const token = u.searchParams.get("t");
  if (!token) return { url: src, token: null };
  u.searchParams.delete("t");
  return { url: u.toString(), token };
}

interface StreamHandle {
  destroy(): void;
}

/** Attach a note's stream to `video`. The caller leaves the element's `src` empty. */
export function attachNoteStream(video: HTMLVideoElement, src: string): StreamHandle {
  const { url, token } = splitMediaToken(src);
  const master = mediaUrl(url, token);
  let hls: Hls | null = null;
  let loading = false;
  let cancelled = false;

  // Why the manifest waits for the play: the media host keeps ONE cookie, and each
  // master request moves it to its own stream. A note that only shows, in a feed or
  // beside the replay, must not take it from the stream that is playing. hls.js is
  // already attached, so the press that plays is the press that loads, and the
  // browser still counts it as the viewer's.
  const load = () => {
    if (loading || !hls) return;
    loading = true;
    hls.loadSource(master);
  };
  const native = () => {
    if (token) video.crossOrigin = "use-credentials";
    video.src = master;
  };

  void import("hls.js")
    .then(({ default: HlsJs }) => {
      if (cancelled) return;
      if (!HlsJs.isSupported()) {
        if (video.canPlayType("application/vnd.apple.mpegurl")) native();
        return;
      }
      const h = new HlsJs(
        token
          ? { enableWorker: true, xhrSetup: (xhr: XMLHttpRequest) => { xhr.withCredentials = true; } }
          : { enableWorker: true },
      );
      // One recovery each, as the Watch page does: a network error reloads the
      // master, which takes the cookie back from whatever stream played since.
      let retriedNetwork = false;
      let retriedMedia = false;
      h.on(HlsJs.Events.ERROR, (_e, data) => {
        if (!data.fatal) return;
        if (data.type === HlsJs.ErrorTypes.NETWORK_ERROR && !retriedNetwork) {
          retriedNetwork = true;
          const at = video.currentTime;
          h.loadSource(master);
          h.startLoad(at);
          return;
        }
        if (data.type === HlsJs.ErrorTypes.MEDIA_ERROR && !retriedMedia) {
          retriedMedia = true;
          h.recoverMediaError();
        }
      });
      h.attachMedia(video);
      hls = h;
      if (video.autoplay || !video.paused) load();
      else video.addEventListener("play", load);
    })
    .catch(() => {
      if (!cancelled && video.canPlayType("application/vnd.apple.mpegurl")) native();
    });

  return {
    destroy() {
      cancelled = true;
      video.removeEventListener("play", load);
      hls?.destroy();
    },
  };
}

/**
 * Play `src` in the `<video>` that `ref` holds. Returns true for a stream, which is
 * attached here: the caller then gives the element no `src` of its own. A file
 * returns false and plays from the caller's `src` as before.
 */
export function useNoteStream(
  ref: RefObject<HTMLVideoElement | null>,
  src: string | null | undefined,
): boolean {
  const stream = isStreamUrl(src);
  useEffect(() => {
    const video = ref.current;
    if (!stream || !video || !src) return;
    const handle = attachNoteStream(video, src);
    return () => handle.destroy();
  }, [ref, src, stream]);
  return stream;
}
